// React hooks for state management and side effects
import { useState, useEffect } from 'react';
// React Router utilities for reading URL parameters, navigation, and links
import { useParams, useNavigate, Link } from 'react-router-dom';
// Custom authentication hook for getting the current user and loading state
import { useAuth } from '../hooks/useAuth';
// Axios instance configured for backend API requests
import axiosClient from '../api/axiosClient';
// Type definition for Course objects
import type { Course } from '../types';
// Toast notifications
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

function CourseDetail() {
  // Destructure user object and loading status from the custom auth hook
  const { user, isLoading } = useAuth();
  // Extract the "id" parameter from the URL
  const { id } = useParams<{ id: string }>();
  // Local state to store course details
  const [course, setCourse] = useState<Course | null>(null);
  // Local state to store error messages
  const [error, setError] = useState<string | null>(null);
  // Local state to track loading status of fetching course details
  const [loading, setLoading] = useState(true);
  // React Router navigation hook for redirecting users
  const navigate = useNavigate();

  // Function to fetch course details from the API
  const fetchCourse = async () => {
    try {
      // Make GET request with CSRF token included from cookies
      const response = await axiosClient.get(`/elearning/api/courses/${id}/`, {
        headers: {
          'X-CSRFToken': document.cookie.match(/csrftoken=([^;]+)/)?.[1] || '',
        },
      });
      // Normalize and store course data in state
      setCourse({
        ...response.data,
        id: Number(response.data.id),
        enrolled: response.data.enrolled || false,
        slug: response.data.slug || `course-${response.data.id}`,
        teacher: {
          ...response.data.teacher,
          id: Number(response.data.teacher.id),
          email: response.data.teacher.email || null,
          real_name: response.data.teacher.real_name || null,
          photo: response.data.teacher.photo || null,
        },
      });
      // Reset error and loading states
      setError(null);
      setLoading(false);
    } catch (err: any) {
      // Extract error message from API response or fallback to generic message
      const errorMessage = err.response?.data?.error || err.message || 'Failed to fetch course';
      setError(errorMessage);
      setLoading(false);
    }
  };
  // useEffect to fetch course or redirect depending on authentication status
  useEffect(() => {
    if (!isLoading && !user) {
      // If not loading and user is not logged in, redirect to login page
      navigate('/login');
    } else if (id) {
      // If an ID exists in the route params, fetch the course
      fetchCourse();
    }
  }, [isLoading, user, id, navigate]);

  // Function to enroll the user in the course
  const handleEnroll = async () => {
    try {
      const response = await axiosClient.post(`/elearning/api/courses/${id}/enroll/`, {}, {
        headers: {
          'X-CSRFToken': document.cookie.match(/csrftoken=([^;]+)/)?.[1] || '',
        },
      });
      await fetchCourse();
      setError(null);
      if (response.data.status === 'enrolled') {
        toast.success('Successfully enrolled!');
      } else {
        toast.info('Already enrolled in the course.');
      }
    } catch (err: any) {
      if (err.response?.status === 500) {
        await fetchCourse();
        toast.warning('Enrollment may have succeeded. If not, please try again.');
      } else {
        toast.error(err.response?.data?.detail || 'Failed to enroll in the course.');
      }
    }
  };
  // Function to unenroll the user from the course
  const handleUnenroll = async () => {
    try {
      const response = await axiosClient.post(`/elearning/api/courses/${id}/unenroll/`, {}, {
        headers: {
          'X-CSRFToken': document.cookie.match(/csrftoken=([^;]+)/)?.[1] || '',
        },
      });
      await fetchCourse();
      setError(null);
      if (response.data.status === 'unenrolled') {
        toast.success('Successfully unenrolled!');
      } else {
        toast.info('Not enrolled in the course.');
      }
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Failed to unenroll from the course.');
    }
  };
  // Show loading spinner if authentication or course data is still loading
  if (isLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-blue-600"></div>
      </div>
    );
  }
  // If no user is logged in, render nothing (navigation already handled above)
  if (!user) return null;
  // If error exists and course data is missing, show error with link back home
  if (error && !course) {
    return (
      <div className="min-h-screen bg-gray-50 font-sans">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="mb-8 p-4 bg-red-50 text-red-700 rounded-lg shadow-sm">
            {error}
          </div>
          <Link
            to="/"
            className="px-4 py-2 bg-gray-200 text-gray-900 rounded hover:bg-gray-300"
          >
            Back to Homepage
          </Link>
        </div>
          <ToastContainer position="top-right" autoClose={3000} hideProgressBar />
      </div>
    );
  }
  // If no error but course not found, show "not found" message
  if (!course) {
    return (
      <div className="min-h-screen bg-gray-50 font-sans">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="mb-8 p-4 bg-red-50 text-red-700 rounded-lg shadow-sm">
            Course not found
          </div>
          <Link
            to="/"
            className="px-4 py-2 bg-gray-200 text-gray-900 rounded hover:bg-gray-300"
          >
            Back to Homepage
          </Link>
        </div>
          <ToastContainer position="top-right" autoClose={3000} hideProgressBar />
      </div>
    );
  }
  // Main course detail UI rendering
return (
  <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 font-sans">
    <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
      {/* Header */}
      <div className="flex justify-between items-center mb-10">
        <h1 className="text-4xl font-extrabold text-gray-900 tracking-tight">
          {course.name}
        </h1>
        <Link
          to="/"
          className="px-5 py-2.5 bg-gray-100 text-gray-800 rounded-xl shadow-sm hover:bg-gray-200 transition"
        >
          ← Back to Homepage
        </Link>
      </div>

      {/* Error message */}
      {error && (
        <div className="mb-8 p-4 bg-red-100 text-red-800 rounded-xl shadow flex items-center">
          <svg
            className="h-5 w-5 mr-2 text-red-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          {error}
        </div>
      )}

      {/* Course content card */}
      <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100">
        {/* Course description */}
        <p className="text-gray-700 mb-6 leading-relaxed text-lg">
          {course.description}
        </p>

        {/* Teacher info */}
        <p className="text-gray-900 font-medium mb-6">
          👨‍🏫 Instructor:{" "}
          <span className="font-semibold">
            {course.teacher.real_name ?? course.teacher.username}
          </span>
        </p>

        {/* Only show materials if user is enrolled or teacher */}
        {course.materials && (course.enrolled || (user && course.teacher.id === user.id)) && (
              <a
                href={course.materials}
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
              >
                View Materials
              </a>
            )}
        {/* Action buttons */}
        <div className="flex flex-wrap gap-4 mt-6">
          {/* Post update */}
          <Link
            to={`/post-status-update/${course.id}`}
            className="px-5 py-2.5 bg-blue-600 text-white rounded-xl shadow hover:bg-blue-700 transition"
          >
            ✏️ Post Update
          </Link>

          {/* Chat */}
          {(course.enrolled ||
            (user.role === "teacher" && course.teacher.id === user.id)) && (
            <Link
              to={`/chat/${course.slug}`}
              className="px-5 py-2.5 bg-green-600 text-white rounded-xl shadow hover:bg-green-700 transition"
            >
              💬 Go to Chat
            </Link>
          )}

          {/* Student actions */}
          {user.role === "student" && (
            <>
              {course.enrolled ? (
                <>
                  <Link
                    to={`/course/provide_feedback/${course.id}`}
                    className="px-5 py-2.5 bg-indigo-600 text-white rounded-xl shadow hover:bg-indigo-700 transition"
                  >
                    ⭐ Provide Feedback
                  </Link>
                  <button
                    onClick={handleUnenroll}
                    className="px-5 py-2.5 bg-red-600 text-white rounded-xl shadow hover:bg-red-700 transition"
                  >
                    ❌ Unenroll
                  </button>
                </>
              ) : (
                <button
                  onClick={handleEnroll}
                  className="px-5 py-2.5 bg-blue-600 text-white rounded-xl shadow hover:bg-blue-700 transition"
                >
                  ✅ Enroll
                </button>
              )}
            </>
          )}

          {/* Teacher actions */}
          {user.role === "teacher" && course.teacher.id === user.id && (
            <>
              <Link
                to={`/courses/delete/${course.id}`}
                className="px-5 py-2.5 bg-red-600 text-white rounded-xl shadow hover:bg-red-700 transition"
              >
                🗑️ Delete Course
              </Link>
              <Link
                to={`/course/view_feedback/${course.id}`}
                className="px-5 py-2.5 bg-indigo-600 text-white rounded-xl shadow hover:bg-indigo-700 transition"
              >
                📊 View Feedback
              </Link>
              <Link
                to={`/manage_students/${course.id}`}
                className="px-5 py-2.5 bg-purple-600 text-white rounded-xl shadow hover:bg-purple-700 transition"
              >
                👥 Manage Students
              </Link>
              <Link
                to={`/create_deadline/${course.id}`}
                className="px-5 py-2.5 bg-teal-600 text-white rounded-xl shadow hover:bg-teal-700 transition"
              >
                ⏰ Create Deadline
              </Link>
            </>
          )}
        </div>
      </div>
    </div>

    {/* Toast container */}
    <ToastContainer
      position="top-right"
      autoClose={3000}
      hideProgressBar
      theme="colored"
    />
  </div>
);

}
export default CourseDetail;